import React from "react";
import { Search, X, Users } from "lucide-react";
import clsx from "clsx";

const PRIORITY_OPTS = ["low", "normal", "high", "critical"];
const STATUS_OPTS = ["planned", "in_progress", "done", "blocked", "spilled"];

export default function FilterBar({
  rooms = [],
  crew = [],
  filters,
  onChange,
  groupMode = "date",
}) {
  const toggle = (key, value) => {
    const cur = filters[key] || [];
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    onChange({ ...filters, [key]: next });
  };

  const setSearch = (v) => onChange({ ...filters, search: v });

  const toggleShowAllCrew = () => onChange({ ...filters, showAllCrew: !filters.showAllCrew });

  const setSchedFilter = (v) => onChange({ ...filters, schedFilter: v });

  const reset = () =>
    onChange({ rooms: [], crew: [], priorities: [], statuses: [], search: "", showAllCrew: false, schedFilter: "all" });

  const schedFilter = filters.schedFilter || "all";
  const activeCount =
    (filters.rooms?.length || 0) +
    (filters.crew?.length || 0) +
    (filters.priorities?.length || 0) +
    (filters.statuses?.length || 0) +
    (filters.search ? 1 : 0) +
    (filters.showAllCrew ? 1 : 0) +
    (schedFilter !== "all" ? 1 : 0);

  return (
    <div
      className="h-10 bg-bg-base border-b border-line flex items-center px-3 gap-2 sticky top-12 z-30 overflow-x-auto"
      data-testid="filter-bar"
    >
      <span className="mono text-2xs uppercase tracking-widest text-ink-mute whitespace-nowrap">FILTER</span>

      {/* Rooms — hidden in ROOMS view since tasks are already grouped by room */}
      {groupMode !== "room" && (
        <>
          <div className="flex items-center gap-1">
            {rooms.map((r) => {
              const active = filters.rooms?.includes(r.id);
              return (
                <button
                  key={r.id}
                  onClick={() => toggle("rooms", r.id)}
                  className={clsx(
                    "flex items-center gap-1 px-2 py-1 border text-2xs uppercase tracking-wider whitespace-nowrap transition-colors",
                    active
                      ? "border-amber text-amber bg-amber-glow"
                      : "border-line text-ink-dim hover:border-line-strong hover:text-ink"
                  )}
                  data-testid={`filter-room-${r.abbreviation || r.name}`}
                >
                  <span
                    className="w-1.5 h-1.5"
                    style={{ background: `var(--room-${r.color_token}, #FFB300)`, backgroundColor: roomColor(r.color_token) }}
                  />
                  {r.abbreviation || r.name}
                </button>
              );
            })}
          </div>
          <div className="h-5 w-px bg-line" />
        </>
      )}

      {/* Scheduled / Unscheduled toggle — only in ROOMS view, where floating
          tasks are rendered inline under their room and producers want to
          temporarily hide one bucket. */}
      {groupMode === "room" && (
        <>
          <div className="flex items-center gap-0" data-testid="filter-sched">
            {[
              { key: "all", label: "ALL" },
              { key: "scheduled", label: "SCHEDULED" },
              { key: "unscheduled", label: "UNSCHEDULED" },
            ].map((opt, i, arr) => {
              const active = schedFilter === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setSchedFilter(opt.key)}
                  className={clsx(
                    "px-2 py-1 border text-2xs uppercase tracking-wider whitespace-nowrap transition-colors",
                    i > 0 && "-ml-px",
                    active
                      ? "border-amber text-amber bg-amber-glow z-10"
                      : "border-line text-ink-dim hover:border-line-strong hover:text-ink"
                  )}
                  data-testid={`filter-sched-${opt.key}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <div className="h-5 w-px bg-line" />
        </>
      )}

      {/* Status */}
      <div className="flex items-center gap-1">
        {STATUS_OPTS.map((s) => {
          const active = filters.statuses?.includes(s);
          return (
            <button
              key={s}
              onClick={() => toggle("statuses", s)}
              className={clsx(
                "px-2 py-1 border text-2xs uppercase tracking-wider whitespace-nowrap transition-colors",
                active
                  ? "border-amber text-amber bg-amber-glow"
                  : "border-line text-ink-dim hover:border-line-strong hover:text-ink"
              )}
              data-testid={`filter-status-${s}`}
            >
              {s.replace("_", " ")}
            </button>
          );
        })}
      </div>

      <div className="h-5 w-px bg-line" />

      {/* Priority */}
      <div className="flex items-center gap-1">
        {PRIORITY_OPTS.map((p) => {
          const active = filters.priorities?.includes(p);
          return (
            <button
              key={p}
              onClick={() => toggle("priorities", p)}
              className={clsx(
                "px-2 py-1 border text-2xs uppercase tracking-wider whitespace-nowrap transition-colors",
                active
                  ? "border-amber text-amber bg-amber-glow"
                  : "border-line text-ink-dim hover:border-line-strong hover:text-ink"
              )}
              data-testid={`filter-priority-${p}`}
            >
              {p}
            </button>
          );
        })}
      </div>

      <div className="h-5 w-px bg-line" />

      {/* Show all crew columns — overrides the auto-hide of crew with no
          assignments in the current window. Useful when adding new
          assignments because empty cells become clickable. */}
      <button
        onClick={toggleShowAllCrew}
        className={clsx(
          "flex items-center gap-1 px-2 py-1 border text-2xs uppercase tracking-wider whitespace-nowrap transition-colors",
          filters.showAllCrew
            ? "border-amber text-amber bg-amber-glow"
            : "border-line text-ink-dim hover:border-line-strong hover:text-ink"
        )}
        title={filters.showAllCrew
          ? `Showing all ${crew.length} crew members — click to show only those with assignments in view`
          : "Show every crew column, even unassigned ones — useful when assigning crew"}
        data-testid="filter-show-all-crew"
      >
        <Users className="w-3 h-3" />
        ALL CREW
        {filters.showAllCrew && crew.length > 0 && <span className="opacity-60">· {crew.length}</span>}
      </button>

      {/* Search */}
      <div className="flex items-center gap-1 ml-auto bg-bg-elevated border border-line px-2 py-1 min-w-[200px]">
        <Search className="w-3 h-3 text-ink-mute" />
        <input
          type="text"
          placeholder="search…"
          value={filters.search || ""}
          onChange={(e) => setSearch(e.target.value)}
          className="inline-input mono text-2xs uppercase tracking-wider placeholder:text-ink-ghost"
          data-testid="filter-search"
        />
      </div>

      {activeCount > 0 && (
        <button
          onClick={reset}
          className="btn-ghost"
          data-testid="filter-reset"
          title="Clear filters"
        >
          <X className="w-3 h-3" />
          RESET · {activeCount}
        </button>
      )}
    </div>
  );
}

export function roomColor(token) {
  const map = {
    magenta: "#FF1A66",
    cyan: "#00E5FF",
    violet: "#B266FF",
    lime: "#5BE36A",
    amber: "#FFB300",
    coral: "#FF7048",
    azure: "#5AA7FF",
    rose: "#FF4A8A",
  };
  return map[token] || "#FFB300";
}
